import type { GameId } from '@chessmate/protocol';
import { GameBackdrop } from '../components/GameBackdrop.js';
import { Button } from '@chessmate/ui';
import { PageHeader } from '../components/PageHeader.js';
import { GameCard } from '../components/GameCard.js';
import { ScreenLayout } from '../components/ScreenLayout.js';
import { useT } from '../i18n/useT.js';
import { editionCatalog, gameEntry } from '../lib/games-catalog.js';
import { useRemoteStages } from '../lib/remote-stage-store.js';

/**
 * The Games page: a card with a cover picture for every game. Picking one selects it and returns to
 * Home, where the robot level and Play button stay, so the common path is unchanged.
 */
export function GamesScreen({ selected, onSelect, onExit }: { selected: GameId; onSelect: (game: GameId) => void; onExit: () => void }) {
  const { t } = useT('games');
  // The operator's remote stages can arrive after the first render: re-render when they do (`editionCatalog()` reads them).
  useRemoteStages();
  return (
    <ScreenLayout width="wide" backdrop={<GameBackdrop game={gameEntry(selected)} />} data-appdom-scope="games" data-testid="games-screen">
      <PageHeader
        title={t('games-title')}
        subtitle={t('games-subtitle')}
        hero="/art/hero-home.webp"
        action={
          <Button variant="secondary" onClick={onExit} data-testid="games-back" data-appdom-id="games-back" data-appdom-type="Button">
            {t('games-back')}
          </Button>
        }
      />
      <section aria-label={t('games-title')} data-testid="games-grid" className="grid grid-cols-1 gap-5 min-[560px]:grid-cols-2 lg:grid-cols-3 lg:gap-6 2xl:grid-cols-4">
        {editionCatalog().map((g) => (
          <GameCard key={g.id} game={g} selected={g.id === selected} onSelect={onSelect} />
        ))}
      </section>
    </ScreenLayout>
  );
}
